import { showAchievementToasts } from '../utils/achievementToast'
import { showUpsell } from '../utils/upsell'

// Returned when a request is answered by a sign-in redirect or an upsell
// modal: the caller has no data to work with and the user is being taken
// somewhere else, so awaiting this simply stops the caller in place. Resolving
// `undefined` instead used to crash components that render `data.length`.
const HALT = new Promise(() => {})

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

function handleUnauthorized(response) {
  if (response.status === 401) {
    window.location.href = '/session/new'
    return true
  }
  // 402 Payment Required → gated behind a subscription. Show the upsell in
  // place rather than navigating away: the redirect discarded the unit_id/level
  // the server sends in the body, so the learner landed on a generic billing
  // page with no memory of what they had been trying to open.
  if (response.status === 402) {
    response.clone().json()
      // fromBlockedPage: reaching a 402 means we are ALREADY on a page the
      // learner cannot use, so dismissing has to take them back rather than
      // leave them staring at a half-loaded screen.
      .then(body => showUpsell({ unitId: body?.unit_id ?? null, level: body?.level ?? null,
                            unitTitle: body?.feature ?? null, fromBlockedPage: true }))
      .catch(() => showUpsell({ fromBlockedPage: true }))
    return true
  }
  return false
}

export async function apiGet(url) {
  const response = await fetch(url, {
    headers: {
      'Accept': 'application/json',
    },
  })
  if (handleUnauthorized(response)) return HALT
  if (!response.ok) throw new Error(`GET ${url} failed: ${response.status}`)
  return response.json()
}

export async function apiPost(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: JSON.stringify(body),
  })
  if (handleUnauthorized(response)) return HALT
  if (!response.ok) throw new Error(`POST ${url} failed: ${response.status}`)
  const data = await response.json()
  if (data && Array.isArray(data.new_achievements)) showAchievementToasts(data.new_achievements)
  return data
}

export async function apiDelete(url) {
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
  })
  if (handleUnauthorized(response)) return HALT
  if (!response.ok) throw new Error(`DELETE ${url} failed: ${response.status}`)
  return response.json()
}

export async function apiPostFile(url, file, fieldName = 'audio') {
  const formData = new FormData()
  formData.append(fieldName, file)

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    body: formData,
  })
  if (handleUnauthorized(response)) return
  if (!response.ok) {
    // Callers branch on status: 422 means the upload itself was rejected
    // (undecodable recording — "try again"), anything else is service trouble.
    const error = new Error(`POST ${url} failed: ${response.status}`)
    error.status = response.status
    throw error
  }
  return response.json()
}
